import {
  Link,
  useHydrated,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { NEWS_PAGE_SIZE, NEWS_TYPES } from '~/lib/news/types'
import type { NewsCardData, NewsPage, NewsType } from '~/lib/news/types'
import { NEWS_TAB_LABELS, TABS, tabPath } from '~/lib/news/routes'
import { newsPageQuery, prefetchNewsPage } from '~/lib/news/pages'
import { useUrlSearchQuery } from '~/hooks/useUrlSearchQuery'
import { formatDate } from '~/lib/date'
import { preloadImages } from '~/lib/media/media-image'
import { ContentCard, GRID_CARD_SIZES } from '~/components/ui/ContentCard'
import { Container } from '~/components/ui/Container'
import { Pagination } from '~/components/ui/Pagination'
import { LinkTabs } from '~/components/ui/Tabs'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { PageTitle, pageTitleClass } from '~/components/ui/PageTitle'
import {
  NewsSearchBox,
  NewsSearchResults,
} from '~/components/sections/NewsSearch'
import { pageTop } from '~/components/ui/PageHero'

type NewsItem = NewsCardData

/** Hover/idle prefetch: the page's JSON into the cache, its card images warm. */
function warmNewsPage(queryClient: QueryClient, type: NewsType, page: number) {
  void prefetchNewsPage(queryClient, type, page).then((data) =>
    preloadImages(data?.items.map((i) => i.image) ?? [], GRID_CARD_SIZES),
  )
}

/**
 * The /news listing for one tab. `initial` is that tab's page 1 (prerendered);
 * `page` comes from the query string, which the static HTML can't know, so it
 * only applies after hydration.
 */
export function NewsListing({
  type,
  page,
  q,
  initial,
}: {
  type: NewsType
  page: number
  /** Search query from `?q=`; replaces the list while set. */
  q?: string
  initial: NewsPage
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const hydrated = useHydrated()
  const hash = useLocation({ select: (l) => l.hash })
  const requestedPage = hydrated ? page : 1
  const tabLabel = NEWS_TAB_LABELS[type]

  // Old Webflow URLs: /news#blog -> /news/blog
  useEffect(() => {
    const old = NEWS_TYPES.find((t) => t === hash)
    if (hydrated && old && type === 'all') {
      void navigate({ ...tabPath(old), hash: undefined, replace: true })
    }
  }, [hydrated, hash, navigate, type])

  // Prerendered HTML has no query, so `q` only applies once hydrated.
  const query = hydrated ? q : undefined
  const search = useUrlSearchQuery(query, (next, replace) => {
    void navigate({
      ...tabPath(type),
      search: { q: next },
      replace,
      resetScroll: false,
    })
  })

  // `initial` is this tab's page 1. While another page loads, the one on
  // screen stays (dimmed) so the list doesn't blank out between pages.
  const pageData = useQuery({
    ...newsPageQuery(type, requestedPage),
    initialData: requestedPage === 1 ? initial : undefined,
    placeholderData: keepPreviousData,
  })
  const loaded = pageData.data
  const loading = pageData.isPlaceholderData || pageData.isPending
  const items = loaded?.items ?? []
  const featured = loaded?.featured

  // Once a page is showing, warm the next one while the browser is idle.
  const nextPage =
    loaded && !loading && loaded.page < loaded.totalPages ? loaded.page + 1 : 0
  useEffect(() => {
    if (!nextPage) return
    const run = () => warmNewsPage(queryClient, type, nextPage)
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(run, { timeout: 3000 })
      return () => cancelIdleCallback(id)
    }
    const id = setTimeout(run, 1000)
    return () => clearTimeout(id)
  }, [queryClient, type, nextPage])
  const pageLink = (p: number) => ({
    ...tabPath(type),
    search: { page: p > 1 ? p : undefined },
  })

  return (
    <>
      <div className="pb-20 max-lg:pb-10">
        <Container className={pageTop}>
          <PageTitle className="mb-5">News</PageTitle>
          <p className="mb-0 text-sm leading-6 md:text-lg md:leading-7">
            Breaking news and publications from Direct Action Everywhere.
          </p>
          <p className="mt-[25px] mb-0 text-sm leading-6 md:text-lg md:leading-7">
            Media inquiry? Please email{' '}
            <a
              href="mailto:press@dxe.io"
              className="font-bold text-accent hover:underline"
            >
              press@dxe.io
            </a>
            .
          </p>

          <div className="flex flex-col gap-3 pt-6 pb-6 lg:flex-row lg:items-center lg:gap-5 lg:pt-10 lg:pb-8">
            <LinkTabs
              label="Filter news"
              activeId={type}
              tabs={TABS.map((t) => ({
                id: t.id,
                label: t.label,
                // A search carries over to the next tab.
                link: { ...tabPath(t.id), search: { q: query } },
              }))}
              fill
              className="lg:w-auto lg:shrink-0"
            />

            <NewsSearchBox
              className="w-full lg:min-w-0 lg:flex-1"
              value={search.input}
              onChange={search.onChange}
              onSubmit={search.submit}
            />
          </div>

          {query && (
            <NewsSearchResults query={query} tab={type} tabLabel={tabLabel} />
          )}

          {!query && featured && (
            <>
              <h2 className={`mt-5 mb-2.5 ${pageTitleClass('sm')}`}>
                Featured
              </h2>
              <NewsCard item={featured} wide priority />
            </>
          )}

          {!query && (
            <>
              <h2
                id="news-list"
                className={`mt-5 mb-2.5 scroll-mt-24 ${pageTitleClass('sm')}`}
              >
                {type === 'all' ? 'All News' : tabLabel}
              </h2>
              <ul
                aria-busy={loading}
                className={`grid grid-cols-1 gap-8 transition-opacity md:grid-cols-2 lg:grid-cols-3 ${loading ? 'opacity-50' : ''}`}
              >
                {items.map((item, i) => (
                  <li key={item.slug}>
                    <NewsCard item={item} priority={i < 3} />
                  </li>
                ))}
              </ul>
              {pageData.isError && (
                <p>
                  That page doesn’t exist.{' '}
                  <Link
                    to="/news"
                    className="font-bold text-accent hover:underline"
                  >
                    Back to all news
                  </Link>
                </p>
              )}
              {loaded && items.length === 0 && <p>No items found.</p>}

              {loaded && (
                <Pagination
                  className="mt-10"
                  page={loaded.page}
                  totalPages={loaded.totalPages}
                  total={loaded.total}
                  pageSize={NEWS_PAGE_SIZE}
                  pageLink={pageLink}
                  scrollToId="news-list"
                  onPrefetch={(p) => warmNewsPage(queryClient, type, p)}
                />
              )}
            </>
          )}
        </Container>
      </div>
      <DonateSignupBand />
    </>
  )
}

function NewsCard({
  item,
  wide,
  priority,
}: {
  item: NewsItem
  wide?: boolean
  priority?: boolean
}) {
  const meta = [formatDate(item.date), item.outlet].filter(Boolean).join(' · ')
  // Blog posts are in their own collection, at /blog.
  const page: LinkProps =
    item.category === 'Blog'
      ? { to: '/blog/$slug', params: { slug: item.slug } }
      : { to: '/dxe-in-the-news/$slug', params: { slug: item.slug } }
  return (
    <ContentCard
      {...(item.url ? { href: item.url, external: true } : { to: page })}
      image={item.image}
      eyebrow={item.category}
      title={item.title}
      meta={meta}
      summary={item.summary}
      cta={item.url ? 'Read article' : 'Read more'}
      wide={wide}
      priority={priority}
      sizes={wide ? '(min-width: 992px) 640px, 100vw' : undefined}
    />
  )
}
